import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStatusPages, useCreateStatusPage, useSettings, useUpdateSettings } from '../../hooks/useStatusPages';
import { useAuth } from '../../context/AuthContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, checkboxClass } from '../common/Input';
import { Skeleton } from '../common/Skeleton';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';

const ACCESS_LABEL = { public: 'Public', passcode: 'Passcode', authenticated: 'Signed-in users' } as const;

/** Slug from a title: "Acme Corp — Web" → "acme-corp-web". */
export function slugify(s: string): string {
  return s.toLowerCase().normalize('NFKD').replace(/[^\w\s-]/g, '').trim().replace(/[\s_]+/g, '-').replace(/-+/g, '-').slice(0, 63);
}

export function StatusPagesPage() {
  useDocumentTitle('Status pages');
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, isLoading } = useStatusPages();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const create = useCreateStatusPage();
  const canEdit = hasPermission('status_pages.edit');
  const canToggle = hasPermission('config.edit');

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({ slug: slug || slugify(title), title }, {
      onSuccess: page => { addToast('success', 'Status page created'); navigate(`/status-pages/${page.id}`); },
      onError: err => addToast('error', apiError(err, 'Could not create the page')),
    });
  };

  const enabled = settings?.['status_pages.enabled'] ?? data?.enabled ?? false;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Status pages</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Public pages your clients can open — each with its own services, branding and access rule.
          </p>
        </div>
      </div>

      <Card className="p-4">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className={`${checkboxClass} mt-0.5`}
            checked={enabled}
            disabled={!canToggle || updateSettings.isPending}
            onChange={e => updateSettings.mutate({ 'status_pages.enabled': e.target.checked }, {
              onSuccess: s => addToast('success', s['status_pages.enabled'] ? 'Status pages are live' : 'Status pages are switched off'),
              onError: err => addToast('error', apiError(err, 'Could not change the setting')),
            })}
          />
          <span>
            <span className="font-medium">Serve status pages</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              The instance-wide switch. Off, every page (and every badge and feed) answers 404, whatever its own setting.
              {!canToggle && ' Only admins can change this.'}
            </span>
          </span>
        </label>
      </Card>

      <Card className="divide-y divide-gray-200 dark:divide-gray-700">
        {isLoading ? (
          <div className="p-4 space-y-3">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (data?.pages ?? []).map(p => (
          <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <Link to={`/status-pages/${p.id}`} className="font-medium hover:underline">{p.title}</Link>
              {p.is_default && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">default</span>}
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">/status/{p.slug}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span>{p.service_count} service{p.service_count === 1 ? '' : 's'}</span>
              <span>{ACCESS_LABEL[p.access]}</span>
              <span className={`px-1.5 py-0.5 rounded font-medium ${p.enabled && enabled ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                {p.enabled && enabled ? 'live' : p.enabled ? 'switched off' : 'disabled'}
              </span>
              <a href={`/status/${p.slug}`} target="_blank" rel="noreferrer" className="text-brand-600 dark:text-brand-400 hover:underline">Open ↗</a>
            </div>
          </div>
        ))}
      </Card>

      {canEdit && (
        <Card className="p-4">
          <form onSubmit={submit} className="space-y-4">
            <h2 className="font-semibold">New status page</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title" required>
                {p => (
                  <Input {...p} required value={title} placeholder="Acme Corp" onChange={e => { setTitle(e.target.value); if (!slugTouched) setSlug(slugify(e.target.value)); }} />
                )}
              </Field>
              <Field label="Slug" required hint="The page lives at /status/<slug>.">
                {p => (
                  <Input {...p} required className="font-mono" value={slug} placeholder="acme" onChange={e => { setSlugTouched(true); setSlug(e.target.value); }} />
                )}
              </Field>
            </div>
            <Button type="submit" loading={create.isPending}>Create page</Button>
          </form>
        </Card>
      )}
    </div>
  );
}
